/* oxlint-disable typescript/consistent-type-assertions */
type NativeBridgeAndroid = Record<string, ((options?: unknown) => void) | undefined>;

interface NativeBridgeIOS {
  postMessage: (input: { action: string; options?: unknown }) => void;
}

type Bridge = NativeBridgeAndroid | NativeBridgeIOS;

export class WebviewManager {
  private get _androidBridge(): NativeBridgeAndroid | undefined {
    return (globalThis as typeof window).nativeBridge;
  }

  private get _iosBridge(): NativeBridgeIOS | undefined {
    return (globalThis as typeof window).webkit?.messageHandlers?.nativeBridge;
  }

  private get _webviewMock(): { action: string; options?: unknown }[] | undefined {
    return (globalThis as typeof window & { __webview?: { action: string; options?: unknown }[] })
      .__webview;
  }

  private get _bridge(): Bridge | undefined {
    return this._androidBridge || this._iosBridge;
  }

  get isWebview(): boolean {
    return Boolean(this._bridge) || Boolean(this._webviewMock);
  }

  _callBridgeMethod(methodName: string, options?: unknown): void {
    // Support mock for testing
    if (this._webviewMock) {
      this._webviewMock.push({ action: methodName, options });
      return;
    }

    if (this._androidBridge) {
      this._androidBridge[methodName]?.(options);
      return;
    }

    if (this._iosBridge) {
      this._iosBridge.postMessage({ action: methodName, options });
    }
  }

  onPageFinishedLoading(): void {
    this._callBridgeMethod('onPageFinishedLoading', 'ready');
  }

  onFlowFinished(organizationId?: string): void {
    this._callBridgeMethod(
      'onFlowFinished',
      organizationId ? JSON.stringify({ organizationID: organizationId }) : 'close'
    );
  }

  onContractSigned(organizationId?: string, pricePlanCode?: string): void {
    this._callBridgeMethod(
      'onContractSigned',
      organizationId && pricePlanCode
        ? JSON.stringify({ organizationID: organizationId, pricePlanCode })
        : 'close'
    );
  }

  onEmailVerified(): void {
    this._callBridgeMethod('onEmailVerified', 'verified');
  }

  onCredentialsSucceeded(): void {
    this._callBridgeMethod('onCredentialsSucceeded', 'succeeded');
  }

  onGoogleAuthBtnClicked(): void {
    this._callBridgeMethod('onGoogleAuthBtnClicked');
  }

  onSignOut(): void {
    this._callBridgeMethod('onSignOut');
  }

  onFreemiumToPaidUpgradeSucceeded(): void {
    this._callBridgeMethod('onFreemiumToPaidUpgradeSucceeded');
  }

  onFreemiumToPaidUpgradeClosed(): void {
    this._callBridgeMethod('onFreemiumToPaidUpgradeClosed');
  }

  onJoinTeamSucceeded(): void {
    this._callBridgeMethod('onJoinTeamSucceeded');
  }

  onDueDiligenceClose(): void {
    this._callBridgeMethod('onDueDiligenceClose');
  }

  onDueDiligenceSucceed(): void {
    this._callBridgeMethod('onDueDiligenceSucceed');
  }

  onConnectionSuccess(): void {
    this._callBridgeMethod('onConnectionSuccess');
  }

  onConnectionFailed(): void {
    this._callBridgeMethod('onConnectionFailed');
  }

  onConnectionCancelled(): void {
    this._callBridgeMethod('onConnectionCancelled');
  }
}

export const webviewManager = new WebviewManager();

declare global {
  interface Window {
    nativeBridge?: NativeBridgeAndroid;
    webkit?: {
      messageHandlers?: {
        nativeBridge?: NativeBridgeIOS;
      };
    };
  }
}
